// @catalog F.006
// @catalog F.030
// useProductDetailDialog — hook compartido por /ventas y /margenes para abrir
// el ProductDetailDialog (de /compras/forecast) desde una fila clickeable.
//
// Las tablas de Ventas/Margenes operan con odoo product_id (numérico), pero
// el ProductDetailDialog espera unified_product_id (string canónico del
// sistema). Este hook:
//   1. Recibe odooId + productName desde la tabla.
//   2. Hace fetch a /forecasting/product-mapping/resolve-by-odoo/:odooId.
//   3. Si el producto está mapeado, abre el dialog con unifiedProductId.
//   4. Si no está mapeado, expone error message (snackbar/alert).
import { useCallback, useState } from "react";
import { apiClient } from "../services/apiClient";

interface ResolveResponse {
  status: string;
  data: { unifiedProductId: string; unifiedName: string } | null;
}

interface DialogState {
  open: boolean;
  unifiedProductId: string | null;
  productName: string | undefined;
}

interface UseProductDetailDialogResult {
  // State del dialog (para pasar a ProductDetailDialog).
  dialogState: DialogState;
  // Handler para click en producto — resuelve odooId → unifiedId → abre dialog.
  openProductDetail: (odooId: number, productName: string) => Promise<void>;
  // Handler para cerrar el dialog.
  closeProductDetail: () => void;
  // Loading mientras se resuelve el mapping (entre click y open).
  resolving: boolean;
  // Error message cuando el producto no está mapeado o falló la resolución.
  resolveError: string | null;
  // Limpia el error message (ej. al cerrar el snackbar).
  clearResolveError: () => void;
}

export function useProductDetailDialog(): UseProductDetailDialogResult {
  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    unifiedProductId: null,
    productName: undefined,
  });
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const openProductDetail = useCallback(
    async (odooId: number, productName: string): Promise<void> => {
      if (!Number.isFinite(odooId) || odooId <= 0) {
        setResolveError("Producto sin identificador válido");
        return;
      }
      setResolving(true);
      setResolveError(null);
      try {
        const response = await apiClient.get<ResolveResponse>(
          `/forecasting/product-mapping/resolve-by-odoo/${odooId}`,
        );
        const data = response.data?.data ?? null;
        if (!data) {
          setResolveError(
            `Producto "${productName}" no tiene mapping unificado en el sistema.`,
          );
          return;
        }
        setDialogState({
          open: true,
          unifiedProductId: data.unifiedProductId,
          productName: data.unifiedName || productName,
        });
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Error al resolver producto";
        setResolveError(msg);
      } finally {
        setResolving(false);
      }
    },
    [],
  );

  const closeProductDetail = useCallback(() => {
    setDialogState((prev) => ({ ...prev, open: false }));
  }, []);

  const clearResolveError = useCallback(() => {
    setResolveError(null);
  }, []);

  return {
    dialogState,
    openProductDetail,
    closeProductDetail,
    resolving,
    resolveError,
    clearResolveError,
  };
}
